import { isDefined, isNotDefined } from '@sgme/fp';
import { useContext } from 'react';

import { useTrackEvent } from '@/App/Blotter/useTrackEvent.ts';
import { OrderRow } from '@/App/tiles/orders/components/OrderRow.tsx';
import {
  StopLossContext,
  useValidateStopLoss,
} from '@/App/tiles/orders/StopLossOrder/StopLossOrderCreationTile/stopLossContext.tsx';
import { SgAmountInput } from '@/components/sg-design-system/components/input/SgAmountInput';
import { OmsFieldErrorWrapper } from '@/components/ui/OmsFieldErrorWrapper.tsx';
import { useAppSelector } from '@/store/hooks.ts';
import {
  selectNotionalCurrencyIndexStopLoss,
  selectOrderEntryByIdFromBlotter,
  selectStopLossErrors,
  selectTradeIdFromStopLossTile,
} from '@/store/orders/stopLoss/selectors.ts';

type StopLossNotionalProps = {
  notional: number | undefined;
  setNotional: (value: number | undefined) => void;
};

export const StopLossEditingNotional = (props: StopLossNotionalProps) => {
  const { notional, setNotional } = props;
  const { setIsModifyingField, tileId } = useContext(StopLossContext);
  const notionalCurrencyIndex = useAppSelector((state) => selectNotionalCurrencyIndexStopLoss(state, tileId));
  const track = useTrackEvent();

  const tradeId = useAppSelector((state) => selectTradeIdFromStopLossTile(state, tileId));
  const orderFromBlotter = useAppSelector((state) =>
    tradeId ? selectOrderEntryByIdFromBlotter(state, tradeId) : undefined,
  );
  const errors = useAppSelector((state) => selectStopLossErrors(state, tileId));

  const notionalError = errors?.notional;

  const currency = isDefined(orderFromBlotter) ? orderFromBlotter?.notionalCurrency : undefined;

  const validateStopLoss = useValidateStopLoss();

  const handleNotionalChange = async (value: number | undefined) => {
    track('Order action', 'Notional changed');
    if (isDefined(orderFromBlotter)) {
      const [currency1, currency2] = isDefined(orderFromBlotter?.currencyPair)
        ? orderFromBlotter.currencyPair.split('/')
        : [];

      if (isNotDefined(currency1) || isNotDefined(currency2)) {
        console.error(`[handleNotionalChange] Invalid currency pair: ${currency1}/${currency2}`);
        return;
      }

      const response = await validateStopLoss({
        notional: value,
        notionalCurrencyIndex: currency === currency1 ? 1 : 2,
      });

      if (response?.data?.validationResponseDetailContent.length === 0) {
        setNotional(value);
      }
    }
  };

  return (
    <OrderRow labelId="order.fields.notional.label">
      <OmsFieldErrorWrapper errors={notionalError}>
        <SgAmountInput
          size="sm"
          value={
            notional ?? (isDefined(orderFromBlotter?.notional) ? (orderFromBlotter?.notional as number) : undefined)
          }
          data-e2e="notional"
          onChange={handleNotionalChange}
          onFocus={() => setIsModifyingField(true)}
          onBlur={() => setIsModifyingField(false)}
          unitBackgroundColor={notionalCurrencyIndex === 2 ? 'warning' : undefined}
          unit={currency}
          state={isDefined(notionalError) ? 'invalid' : undefined}
        />
      </OmsFieldErrorWrapper>
    </OrderRow>
  );
};
